import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { useUpdateBewerber } from '../../../api/bewerber';
import { useSendBewerberEmail } from '../../../api/email';
import { BewerberStatus } from '../../../types/bewerber';
import { useAuth } from '../../../context/AuthContext';

/**
 * Minimal Bewerber shape required by the invite flow. Both the full `Bewerber`
 * type and the slimmer `MatchingBewerber` (from the Bedarf matching list)
 * structurally satisfy this interface.
 */
export interface InvitableBewerber {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  status: string;
  hasInvitationEmails?: boolean;
}

const EINLADUNG_TEMPLATE = 'bewerber-einladen';

const BOOKING_URLS: Record<string, string> = {
  'roland.kompalka@juniva.de':
    'https://outlook.office.com/book/Termine@juniva.de/?ismsaljsauthenabled',
  'matthaeus.fattout@juniva.de':
    'https://outlook.office365.com/owa/calendar/JunivaTermin@juniva.de/bookings/',
  'julia.bahner@juniva.de':
    'https://outlook.office.com/book/JunivaTermine@juniva.de/?ismsaljsauthenabled',
  'celina.bester@juniva.de':
    'https://outlook.office.com/book/JunivaTermin1@juniva.de/?ismsaljsauthenabled',
};

const DEFAULT_BOOKING_URL = BOOKING_URLS['roland.kompalka@juniva.de'];

function getBookingUrl(userEmail?: string | null): string {
  if (userEmail && BOOKING_URLS[userEmail]) return BOOKING_URLS[userEmail];
  return DEFAULT_BOOKING_URL;
}

export interface EinladenModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  bewerberName: string;
  isLoading: boolean;
}

export interface UseEinladenBewerberReturn {
  /**
   * Trigger the invite flow for a Bewerber. If the Bewerber already has invitation
   * emails (`hasInvitationEmails === true`), the EinladenModal opens for confirmation
   * first; otherwise the email-then-status flow runs immediately.
   *
   * The status is only changed if the invitation email actually succeeds. A failed
   * email send aborts the status change.
   */
  invite: (bewerber: InvitableBewerber, onSuccess?: () => void) => void;

  /** Spread these onto an `<EinladenModal />` to render the re-invite confirmation. */
  modalProps: EinladenModalProps;

  /** True while an invite flow is in progress (email send or status update). */
  isInviting: boolean;
}

/**
 * Shared hook that owns the "Einladen" (invitation) flow for a Bewerber.
 *
 * Single source of truth used by:
 * - `useBewerberActions` (action button + context menu)
 * - `KanbanBoard` (drag-and-drop into the EINGELADEN column)
 * - `MatchingBewerberSection` (Bedarf detail page invite button)
 *
 * Guarantees:
 * 1. The invitation email is always sent before the status is changed.
 * 2. If the email send fails, the status change is NOT performed.
 * 3. If the Bewerber has no email address, the status change is NOT performed
 *    (the user has to add an email first or change the status via the edit-page
 *    dropdown explicitly).
 * 4. Repeat invitations (`bewerber.hasInvitationEmails === true`) require an
 *    explicit confirmation via the `EinladenModal`.
 */
export function useEinladenBewerber(): UseEinladenBewerberReturn {
  const { user } = useAuth();
  const sendEmailMutation = useSendBewerberEmail();
  const updateMutation = useUpdateBewerber();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  const [pendingBewerber, setPendingBewerber] = useState<InvitableBewerber | null>(null);
  const [pendingOnSuccess, setPendingOnSuccess] = useState<(() => void) | null>(null);

  const runInviteFlow = useCallback(
    async (bewerber: InvitableBewerber, onSuccess?: () => void) => {
      if (!bewerber.email) {
        toast.warning(
          'Bewerber hat keine E-Mail-Adresse - Einladung kann nicht gesendet werden',
        );
        return;
      }

      setIsInviting(true);
      try {
        // Step 1: send the invitation email. If this throws, abort — do NOT change status.
        try {
          await sendEmailMutation.mutateAsync({
            bewerberId: bewerber.id,
            data: {
              templateName: EINLADUNG_TEMPLATE,
              data: {
                senderName: user ? `${user.firstName} ${user.lastName}` : 'Juniva Team',
                bookingUrl: getBookingUrl(user?.email),
              },
            },
          });
        } catch (emailError: any) {
          console.error('Failed to send Einladung email:', emailError);
          const message =
            emailError?.response?.data?.message ||
            'E-Mail konnte nicht gesendet werden - Status wurde nicht geändert';
          toast.error(Array.isArray(message) ? message.join(', ') : message);
          return;
        }

        // Step 2: only on email success, advance the status to EINGELADEN.
        if (bewerber.status !== BewerberStatus.EINGELADEN) {
          try {
            await updateMutation.mutateAsync({
              id: bewerber.id,
              data: { status: BewerberStatus.EINGELADEN },
            });
          } catch (statusError: any) {
            console.error('Failed to update status after sending Einladung:', statusError);
            const message =
              statusError?.response?.data?.message ||
              'E-Mail wurde gesendet, aber Status konnte nicht geändert werden';
            toast.error(Array.isArray(message) ? message.join(', ') : message);
            return;
          }
        }

        toast.success('Bewerber erfolgreich eingeladen');
        onSuccess?.();
      } finally {
        setIsInviting(false);
      }
    },
    [sendEmailMutation, updateMutation, user],
  );

  const invite = useCallback(
    (bewerber: InvitableBewerber, onSuccess?: () => void) => {
      if (bewerber.hasInvitationEmails) {
        // Repeat invitation - require explicit confirmation
        setPendingBewerber(bewerber);
        setPendingOnSuccess(() => onSuccess ?? null);
        setIsModalOpen(true);
        return;
      }
      void runInviteFlow(bewerber, onSuccess);
    },
    [runInviteFlow],
  );

  const handleClose = useCallback(() => {
    if (isInviting) return; // don't close mid-request
    setIsModalOpen(false);
    setPendingBewerber(null);
    setPendingOnSuccess(null);
  }, [isInviting]);

  const handleConfirm = useCallback(() => {
    if (!pendingBewerber) return;
    const bewerber = pendingBewerber;
    const onSuccess = pendingOnSuccess ?? undefined;
    setIsModalOpen(false);
    void runInviteFlow(bewerber, onSuccess).finally(() => {
      setPendingBewerber(null);
      setPendingOnSuccess(null);
    });
  }, [pendingBewerber, pendingOnSuccess, runInviteFlow]);

  const modalProps: EinladenModalProps = {
    isOpen: isModalOpen,
    onClose: handleClose,
    onConfirm: handleConfirm,
    bewerberName: pendingBewerber
      ? `${pendingBewerber.firstName} ${pendingBewerber.lastName}`
      : '',
    isLoading: isInviting,
  };

  return {
    invite,
    modalProps,
    isInviting,
  };
}
